<template>
  <portal
    to="modal"
    :disabled="isPortalDisabled"
    :data-preload="[$options.name]"
  >
    <transition name="simple-dialog">
      <article
        v-if="active"
        ref="dialog"
        class="simple-dialog"
        :class="[
          parentClassName,
          classes,
          {
            'simple-dialog--background': background,
            'simple-dialog--top': position === 'top',
            'simple-dialog--center': position === 'center',
            'simple-dialog--bottom': position === 'bottom',
          },
        ]"
      >
        <div class="simple-dialog__header">
          <template v-if="$slots.headerPrefix">
            <slot name="headerPrefix" />
          </template>
          <template v-else>
            <span class="simple-dialog__title">{{ title }}</span>
            <p v-if="message" class="simple-dialog__message">{{ message }}</p>
          </template>
          <button
            class="simple-dialog__close-button qa-simpleDialogCloseButton"
            :class="gtmClassName ? gtmClassName + 'closeButton' : ''"
            type="button"
            @click="handleClose"
          >
            <CloseSvg class="simple-dialog__close-icon" />
          </button>
        </div>
        <div
          class="simple-dialog__body"
          :class="{ 'simple-dialog__body--slim': removeBodyMargin }"
        >
          <slot />
        </div>
        <Button
          v-if="action ? action.enabled !== false : false"
          class="simple-dialog__action-button qa-simpleDialogActionButton"
          :title="action.title"
          :modifier="action.modifier"
          :on-click="handleActionClick"
          type="button"
        />
      </article>
    </transition>

    <div
      v-if="active"
      class="simple-dialog-shadow"
      :class="{ 'simple-dialog-shadow--background': background }"
    ></div>
  </portal>
</template>

<script>
import scrollMixin from 'Libs/mixins/scrollMixin';
import globalMixin from 'Libs/mixins/globalMixin';
import DeviceHelper from 'Libs/helpers/device';

import CloseSvg from 'Components/00-generated/CloseSvg';
import Button from 'Components/01-atoms/button/Button';

export default {
  name: 'SimpleDialog',
  components: { Button, CloseSvg },
  mixins: [scrollMixin, globalMixin],
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: 'top',
    },
    background: {
      type: Boolean,
      default: false,
    },
    headerIcon: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    removeBodyMargin: {
      type: Boolean,
      default: false,
    },
    action: {
      type: [Object, Boolean],
      default: () => ({
        enabled: false,
        title: '',
        modifier: 'primary',
      }),
    },
  },
  data() {
    return {
      isPortalDisabled: true,
    };
  },
  computed: {
    parentClassName() {
      return this.$options.parent?.$el?.getAttribute('class') || '';
    },
  },
  watch: {
    active() {
      if (this.active) {
        this.disableBodyScrolling();
      } else {
        this.enableBodyScrolling();
      }
    },
  },
  mounted() {
    /*
      Vue-Portal is disabled by default except in iOS device
      since otherwise it does not behave as expected.
    */
    const deviceHelper = new DeviceHelper();
    this.isPortalDisabled = deviceHelper.isIosSafari();
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    handleActionClick() {
      this.$emit('action-click');
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';

.simple-dialog-shadow {
  background: rgba(0, 0, 0, 0.3);
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 20;

  &--background {
    z-index: 100;
  }
}

.simple-dialog {
  position: fixed;
  top: var(--dialog-vertical-distance);
  left: 50%;
  transform: translateX(-50%);
  width: var(--dialog-width);
  padding: 25px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.16);
  z-index: 30;
  background: var(--color-white);
  border-radius: var(--border-radius-default);

  &-enter-active {
    opacity: 0;
    top: 0;
    transition: opacity var(--time-XS) ease-in, top var(--time-XS) ease-in;
  }

  &-enter-to {
    opacity: 1;
    top: 90px;
  }

  &-leave-active {
    opacity: 1;
    top: 90px;
    transition: opacity var(--time-XS) ease-in, top var(--time-XS) ease-in;
  }

  &-leave-to {
    opacity: 0;
    top: 0;
  }

  &--background {
    z-index: 200;
  }

  &--top {
    top: var(--dialog-vertical-distance);
  }

  &--bottom {
    bottom: var(--dialog-vertical-distance);
  }

  &--center {
    top: 50%;
    transform: translate(-50%, -50%);
  }

  --dialog-vertical-distance: 90px;
  --dialog-width: 790px;
  --title-font-size: var(--font-size-L);
  --title-line-height: var(--font-size-LXL);
  --message-font-size: var(--font-size-M);
  --message-line-height: var(--font-size-ML);
  --body-margin-top: 48px;
}

.simple-dialog__title {
  font-size: var(--title-font-size);
  line-height: var(--title-line-height);
  font-weight: bold;
}

.simple-dialog__close-button {
  position: absolute;
  right: 25px;
  top: 25px;
  cursor: pointer;
}

.simple-dialog__close-icon {
  fill: var(--color-nero);
  height: 25px;
  width: 25px;
}

.simple-dialog__message {
  font-size: var(--message-font-size);
  line-height: var(--message-line-height);
  color: var(--color-nero);
  margin-top: 5px;
}

.simple-dialog__body {
  margin-top: var(--body-margin-top);

  &--slim {
    margin-top: 0;
  }
}

.simple-dialog__action-button {
  display: block;
  margin: 50px auto 0;
}

@media #{$_mediaMDown} {
  .simple-dialog {
    --dialog-vertical-distance: #{var(--space-8)};
    --dialog-width: 100%;
    --title-font-size: var(--font-size-M);
    --title-line-height: var(--font-size-ML);
    --message-font-size: var(--font-size-S);
    --message-line-height: var(--font-size-SM);
    --body-margin-top: #{var(--space-2)};

    padding: var(--space-2);
    top: var(--dialog-vertical-distance);
    left: 0;
    transform: none;
    max-height: calc(100% - var(--dialog-vertical-distance));
    overflow-y: auto;
    transition: none;
    width: 100%;
  }

  .simple-dialog__close-button {
    right: var(--space-1);
    top: var(--space-2);
    padding: 0;
  }

  .simple-dialog__close-icon {
    height: 20px;
    width: 20px;
  }

  .simple-dialog__action-wrapper {
    width: 100%;
  }

  .simple-dialog__action-button {
    width: 100%;
    margin-top: var(--space-4);
  }
}
</style>
